import { useState, useRef } from 'react';
import api from '../lib/api';
import { Loader2, Upload, CheckCircle2, AlertTriangle, PlayCircle, ShieldQuestion, Wrench } from 'lucide-react';

// JM Import wizard - admin UI for the Phase 8 backend importer (/api/import/jm/*).
// Three explicit steps: Validate -> Dry run -> Execute. Nothing writes until step 3.
// Below it: the repair tool (/api/import/jm/repair/*), which fixes records written
// by earlier importer builds that omitted the app's UUID `id` field.

const CountTable = ({ title, obj }) => {
  const entries = Object.entries(obj || {}).filter(([, v]) => typeof v === 'number');
  if (entries.length === 0) return null;
  return (
    <div>
      <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1">{title}</p>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1 text-sm">
        {entries.map(([k, v]) => (
          <div key={k} className="flex justify-between border-b border-slate-100 pb-0.5">
            <span className="text-slate-500">{k}</span>
            <span className={`font-medium ${v > 0 ? 'text-slate-900' : 'text-slate-300'}`}>{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const Issues = ({ label, items, tone }) => {
  if (!items || items.length === 0) return null;
  const toneCls = tone === 'error' ? 'text-red-600 bg-red-50' : 'text-amber-700 bg-amber-50';
  return (
    <div className={`rounded-lg p-3 text-xs ${toneCls} space-y-1 max-h-48 overflow-auto`}>
      <p className="font-semibold">{label} ({items.length})</p>
      {items.slice(0, 50).map((e, i) => (
        <p key={i}>{typeof e === 'string' ? e : JSON.stringify(e)}</p>
      ))}
      {items.length > 50 && <p>...and {items.length - 50} more</p>}
    </div>
  );
};

const RepairSummary = ({ r }) => (
  <div className="space-y-3">
    <CountTable title="Ids to backfill" obj={r.ids_backfilled} />
    <CountTable title="User references to remap" obj={r.user_refs_remapped} />
    <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-1 text-sm">
      {[
        ['requirements ctc fixed', r.requirements_ctc_fixed],
        ['requirements jd mirrored', r.requirements_jd_mirrored],
        ['clients contact mirrored', r.clients_contact_mirrored],
        ['duplicate logs removed', r.activity_log_duplicates_removed],
      ].map(([k, v]) => (
        <div key={k} className="flex justify-between border-b border-slate-100 pb-0.5">
          <span className="text-slate-500">{k}</span>
          <span className={`font-medium ${v > 0 ? 'text-slate-900' : 'text-slate-300'}`}>{v}</span>
        </div>
      ))}
    </div>
    <CountTable title="Already healthy" obj={r.already_healthy} />
    <Issues label="Warnings" items={r.warnings} tone="warn" />
  </div>
);

export default function JMImportPage() {
  const fileRef = useRef(null);
  const [fileName, setFileName] = useState('');
  const [busy, setBusy] = useState('');
  const [validation, setValidation] = useState(null);
  const [dryRun, setDryRun] = useState(null);
  const [execResult, setExecResult] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState('');

  // repair tool
  const [repairDry, setRepairDry] = useState(null);
  const [repairResult, setRepairResult] = useState(null);
  const [repairConfirmed, setRepairConfirmed] = useState(false);
  const [repairError, setRepairError] = useState('');

  const post = async (path, extra = {}) => {
    const f = fileRef.current?.files?.[0];
    if (!f) throw new Error('Choose the JM export ZIP first');
    const fd = new FormData();
    fd.append('file', f);
    const params = new URLSearchParams(extra).toString();
    const url = `/import/jm/${path}${params ? `?${params}` : ''}`;
    const res = await api.post(url, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 300000 });
    return res.data;
  };

  const run = async (step) => {
    setBusy(step);
    setError('');
    try {
      if (step === 'validate') {
        setValidation(null); setDryRun(null); setExecResult(null); setConfirmed(false);
        setValidation(await post('validate'));
      } else if (step === 'dry-run') {
        setDryRun(await post('dry-run'));
      } else if (step === 'execute') {
        setExecResult(await post('execute', { dry_run_confirmed: 'true' }));
      }
    } catch (err) {
      setError(err.response?.data?.detail ? JSON.stringify(err.response.data.detail) : String(err.message || err));
    } finally {
      setBusy('');
    }
  };

  const runRepair = async (step) => {
    setBusy(step);
    setRepairError('');
    try {
      if (step === 'repair-dry') {
        setRepairDry(null); setRepairResult(null); setRepairConfirmed(false);
        const res = await api.post('/import/jm/repair/dry-run', null, { timeout: 300000 });
        setRepairDry(res.data);
      } else {
        const res = await api.post('/import/jm/repair/execute?confirmed=true', null, { timeout: 300000 });
        setRepairResult(res.data);
      }
    } catch (err) {
      setRepairError(err.response?.data?.detail ? JSON.stringify(err.response.data.detail) : String(err.message || err));
    } finally {
      setBusy('');
    }
  };

  const canDryRun = validation?.is_valid;
  const canExecute = !!dryRun && confirmed;

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-5" data-testid="jm-import-page">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">JM Import</h1>
        <p className="text-xs text-slate-500 mt-0.5">
          Bring Joining Mail data into this system. Validate, dry run, then execute. Imports are idempotent — running the same file twice never duplicates records.
        </p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
        <div className="flex items-center gap-3">
          <input
            ref={fileRef}
            type="file"
            accept=".zip"
            onChange={(e) => { setFileName(e.target.files?.[0]?.name || ''); setValidation(null); setDryRun(null); setExecResult(null); setConfirmed(false); }}
            className="text-sm text-slate-600 file:mr-3 file:px-3 file:py-1.5 file:rounded-lg file:border-0 file:bg-slate-900 file:text-white file:text-xs hover:file:bg-slate-700"
            data-testid="jm-import-file"
          />
          {fileName && <span className="text-xs text-slate-500">{fileName}</span>}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => run('validate')}
            disabled={!!busy}
            className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-700 disabled:opacity-40"
            data-testid="jm-validate-btn"
          >
            {busy === 'validate' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            1. Validate
          </button>
          <button
            onClick={() => run('dry-run')}
            disabled={!canDryRun || !!busy}
            className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-700 disabled:opacity-40"
            data-testid="jm-dryrun-btn"
          >
            {busy === 'dry-run' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldQuestion className="w-3.5 h-3.5" />}
            2. Dry run
          </button>
          <label className="flex items-center gap-1.5 text-xs text-slate-600 ml-2">
            <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} disabled={!dryRun} data-testid="jm-confirm-check" />
            I reviewed the dry run
          </label>
          <button
            onClick={() => run('execute')}
            disabled={!canExecute || !!busy}
            className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-40"
            data-testid="jm-execute-btn"
          >
            {busy === 'execute' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PlayCircle className="w-3.5 h-3.5" />}
            3. Execute import
          </button>
        </div>

        {error && (
          <div className="flex items-start gap-2 p-3 text-xs text-red-600 bg-red-50 rounded-lg">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <span className="break-all">{error}</span>
          </div>
        )}
      </div>

      {validation && (
        <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
          <div className="flex items-center gap-2">
            {validation.is_valid
              ? <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              : <AlertTriangle className="w-4 h-4 text-red-600" />}
            <h2 className="text-sm font-semibold text-slate-800">
              Validation {validation.is_valid ? 'passed' : 'failed'}
            </h2>
          </div>
          <CountTable title="Entities in file" obj={validation.entity_counts} />
          <Issues label="Errors" items={validation.errors} tone="error" />
          <Issues label="Warnings" items={validation.warnings} tone="warn" />
        </div>
      )}

      {dryRun && (
        <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
          <h2 className="text-sm font-semibold text-slate-800">Dry run — nothing written yet</h2>
          <CountTable title="Would create" obj={dryRun.would_create} />
          <CountTable title="Would merge into existing" obj={dryRun.would_merge} />
          <CountTable title="Would skip (already imported)" obj={dryRun.would_skip} />
          {dryRun.user_mapping && dryRun.user_mapping.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1">User mapping</p>
              <div className="text-xs text-slate-600 space-y-0.5 max-h-40 overflow-auto">
                {dryRun.user_mapping.map((u, i) => (
                  <p key={i}>{typeof u === 'string' ? u : JSON.stringify(u)}</p>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {execResult && (
        <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
          <div className="flex items-center gap-2">
            {execResult.success
              ? <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              : <AlertTriangle className="w-4 h-4 text-amber-500" />}
            <h2 className="text-sm font-semibold text-slate-800">
              Import {execResult.success ? 'completed cleanly' : 'completed with notes'}
            </h2>
            {execResult.import_id && <span className="text-[10px] text-slate-400">id {execResult.import_id}</span>}
          </div>
          <CountTable title="Created" obj={execResult.created} />
          <CountTable title="Merged" obj={execResult.merged} />
          <CountTable title="Skipped" obj={execResult.skipped} />
          <Issues label="Row-level notes" items={execResult.errors} tone="warn" />
        </div>
      )}

      {/* ---------------- Repair tool ---------------- */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
        <div className="flex items-center gap-2">
          <Wrench className="w-4 h-4 text-slate-500" />
          <h2 className="text-sm font-semibold text-slate-800">Repair imported records</h2>
        </div>
        <p className="text-xs text-slate-500">
          Earlier importer builds wrote records without this system's internal id, which left them
          unlinked in the UI (requirements showed under &ldquo;Unknown Client&rdquo;, detail links did nothing).
          This backfills those ids, reconnects owner and assignee references, restores the salary
          currency, and fills in the field names the pages read. It changes no business values, and
          it is safe to run more than once — a second run reports zero.
        </p>

        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => runRepair('repair-dry')}
            disabled={!!busy}
            className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-700 disabled:opacity-40"
            data-testid="jm-repair-dry-btn"
          >
            {busy === 'repair-dry' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldQuestion className="w-3.5 h-3.5" />}
            Check what needs repair
          </button>
          <label className="flex items-center gap-1.5 text-xs text-slate-600 ml-2">
            <input
              type="checkbox"
              checked={repairConfirmed}
              onChange={(e) => setRepairConfirmed(e.target.checked)}
              disabled={!repairDry}
              data-testid="jm-repair-confirm"
            />
            I reviewed the check
          </label>
          <button
            onClick={() => runRepair('repair-exec')}
            disabled={!repairDry || !repairConfirmed || !!busy}
            className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-40"
            data-testid="jm-repair-exec-btn"
          >
            {busy === 'repair-exec' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PlayCircle className="w-3.5 h-3.5" />}
            Apply repair
          </button>
        </div>

        {repairError && (
          <div className="flex items-start gap-2 p-3 text-xs text-red-600 bg-red-50 rounded-lg">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <span className="break-all">{repairError}</span>
          </div>
        )}

        {repairDry && (
          <div className="border-t border-slate-100 pt-4 space-y-3">
            <h3 className="text-xs font-semibold text-slate-700">Check result — nothing written yet</h3>
            <RepairSummary r={repairDry} />
          </div>
        )}

        {repairResult && (
          <div className="border-t border-slate-100 pt-4 space-y-3">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              <h3 className="text-xs font-semibold text-slate-700">Repair applied</h3>
              {repairResult.finished_at && (
                <span className="text-[10px] text-slate-400">{repairResult.finished_at}</span>
              )}
            </div>
            <RepairSummary r={repairResult} />
          </div>
        )}
      </div>
    </div>
  );
}
